import utilities from '@/shared/utilities.service';
import {
    ClickAndCollectPurchaseOption,
    ProductClickAndCollectStoreInventoryResult,
    ProductClientService, PurchaseOptionType,
} from '@/types/productServerApi';
import { computed, ComputedRef, reactive } from 'vue';
import { LocationClientService, StoreModel } from '@/types/locationServerApi';
import useProductDetailData from '@/shared/product/details/useProductDetailData';
import pageStore from '@/shared/content/page.store';

const state: IClickAndCollectState = reactive({
    showStoreSelector: false,
    showStoreInfo: false,
    storeInventoryData: undefined as (ProductClickAndCollectStoreInventoryResult[] | undefined),
    storeId: null as (null | string),
    addToBasketOnStoreSelection: false,
    storeInfo: null,
    amountOfStoresWithStock: null,
});

export default function useProductClickAndCollect() {
    const { productData } = useProductDetailData();

    const addToBasketOnStoreSelection: ComputedRef<boolean> = computed(() => state.addToBasketOnStoreSelection);
    const clickAndCollectStoreInfo: ComputedRef<StoreModel | null | undefined> = computed(() => state.storeInfo);
    const amountOfStoresWithStock: ComputedRef<number | null | undefined> = computed(() => state.amountOfStoresWithStock);
    const basketId = computed(() => pageStore.basketId);

    const setAddToBasketOnStoreSelection = (value: boolean) => state.addToBasketOnStoreSelection = value;

    const getClickAndCollectStatus = async(itemId: string): Promise<(ProductClickAndCollectStoreInventoryResult | undefined)[] | undefined> => {
        const result = ProductClientService.getClickAndCollectStoreInventoryStatus(itemId, basketId.value).then(utilities.unwrapModel);
        return result || [] as (ProductClickAndCollectStoreInventoryResult | undefined)[];
    };

    const setClickAndCollectStoreInfo = async(): Promise<void> => {
        state.storeInfo = await LocationClientService.getStoreById(storeId.value).then(utilities.unwrapModel);
    };

    const storeId: ComputedRef<string | null> = computed(() => {
        return state.storeId;
    });

    const setStoreId = (storeId: string): void => {
        state.storeId = storeId;
    };

    const showStoreInfo: ComputedRef<boolean> = computed(() => state.showStoreInfo);

    const openStoreInfo = (): void => {
        state.showStoreInfo = true;
    };

    const closeStoreInfo = (): void => {
        state.showStoreInfo = false;
    };

    const showStoreSelector: ComputedRef<boolean> = computed(() => state.showStoreSelector);

    const openStoreSelector = (): void => {
        state.showStoreSelector = true;
    };

    const closeStoreSelector = (): void => {
        state.showStoreSelector = false;
    };

    const setStoreInventoryData = async(itemId: string) => {
        const response = await getClickAndCollectStatus(itemId);
        state.storeInventoryData = response?.filter((store) => store !== undefined) as ProductClickAndCollectStoreInventoryResult[];
        setAmountOfStoreWithStock();
    };

    const setAmountOfStoreWithStock = () => {
        let storesWithStock = 0;
        const clickAndCollectOption = productData.value.purchasePossibilities?.find((option) => option.type === PurchaseOptionType.ClickAndCollect);
        if ((clickAndCollectOption as ClickAndCollectPurchaseOption)?.backOrderAvailable) {
            storesWithStock = storeInventoryData.value ? storeInventoryData.value.length : 0;
        } else if (storeInventoryData.value) {
            storeInventoryData.value.forEach((store) => {
                if (store?.status.namedParams.storeInventoryCount) {
                    storesWithStock++;
                }
            });
        }
        state.amountOfStoresWithStock = storesWithStock;
    };

    const storeInventoryData: ComputedRef<(ProductClickAndCollectStoreInventoryResult[] | undefined)> = computed(() => state.storeInventoryData);

    return {
        addToBasketOnStoreSelection,
        amountOfStoresWithStock,
        clickAndCollectStoreInfo,
        showStoreSelector,
        showStoreInfo,
        storeId,
        storeInventoryData,
        closeStoreInfo,
        closeStoreSelector,
        getClickAndCollectStatus,
        openStoreInfo,
        openStoreSelector,
        setAddToBasketOnStoreSelection,
        setClickAndCollectStoreInfo,
        setStoreId,
        setStoreInventoryData,
    };
}

export interface IClickAndCollectState {
    showStoreInfo: boolean;
    showStoreSelector: boolean;
    storeInventoryData: ProductClickAndCollectStoreInventoryResult[] | undefined;
    storeId: null | string;
    addToBasketOnStoreSelection: boolean;
    storeInfo: StoreModel | null | undefined;
    amountOfStoresWithStock: number | null;
}
